import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';

import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';
import { entityExportsApi } from 'src/Services/API';
import {
  CreateEntityExportDtoEntityTypeEnum,
  CreateEntityExportDtoTypeEnum,
  EntityExportDto,
} from 'src/Services/API/generated';
import { trackEvent } from 'src/Services/Matomo';
import { ApiError } from 'src/types/api-error.types';

export interface CreatePracticalInformationExportParams {
  entityIds: string[];
  expiresAt?: string;
}

export const useCreatePracticalInformationExport = (
  options?: UseMutationOptions<EntityExportDto, ApiError, CreatePracticalInformationExportParams>
) => {
  const assetId = useCurrentAssetId();
  const queryClient = useQueryClient();

  return useMutation<EntityExportDto, ApiError, CreatePracticalInformationExportParams>(
    ['create-practical-information-export', assetId],
    async ({ entityIds, expiresAt }) => {
      if (!assetId) {
        throw new Error('No asset selected');
      }
      const dto = {
        entityType: CreateEntityExportDtoEntityTypeEnum.PracticalInformation,
        type: CreateEntityExportDtoTypeEnum.Static,
        entityIds,
        isPublic: true,
        expiresAt,
      };

      const response = await entityExportsApi.entityExportsControllerCreate(assetId, dto);
      return response.data;
    },
    {
      ...options,
      onSuccess: (data, variables, context) => {
        trackEvent('Export', 'create', 'practical-information');
        queryClient.invalidateQueries(['fetch-practical-information-exports', assetId]);
        options?.onSuccess?.(data, variables, context);
      },
    }
  );
};
